<template>
    <div class="vendor-note">
        <b-card no-body class="shadow-sm rounded-lg overflow-hidden">
            <template #header>
                <div class="w-100 d-flex align-items-center">
                    <div class="w-40 d-flex flex-column justify-content-start align-items-start">
                        <div class="w-100">
                            <span 
                                v-if="!localEdit" 
                                @dblclick="enterEditMode" 
                                class="font-size-110 font-weight-600 text-primary-darker"
                            >{{ localNote.title }}</span>
                            <b-form-input 
                                v-if="localEdit"
                                v-model="localNote.title"
                                type="text"
                                class="note-title-input p-1 w-100"
                                :disabled="loadingSave"
                                @input="validateForm"
                            ></b-form-input>
                        </div>
                    </div>
                    <div class="w-30 text-left px-2 d-flex align-items-center">
                        <small v-if="localNote.created_by" class="text-dark">{{ localNote.created_by.full_name }}</small>
                        <small v-if="localNote.created_by" class="mx-2">&middot;</small>
                        <small class="text-secondary">{{ formattedCreationDate }}</small>
                    </div>
                    <div class="w-30 d-flex align-items-center justify-content-end gap-2">
                        <b-button 
                            v-if="isNoteEditable && !localEdit" 
                            key="toEditButton" 
                            variant="subtle-primary"
                            size="sm"
                            :disabled="loadingSave"
                            @click="enterEditMode" 
                        >
                            <b-icon icon="pencil" class="mr-2"></b-icon>
                            <span>{{ $t('vendorNoteLine.edit') }}</span>
                        </b-button>
                        <b-button 
                            v-if="localEdit" 
                            key="saveButton"  
                            variant="success"
                            size="sm"
                            :disabled="loadingSave"
                            @click="handleNoteSave"
                        >
                            <!-- <b-icon icon="save" class="mr-2"></b-icon> -->
                            <span class="">{{ $t('buttons.save') }}</span>
                        </b-button>
                        <b-button 
                            v-if="localEdit" 
                            key="cancelEditButton" 
                            variant="secondary"
                            size="sm"
                            :disabled="loadingSave"
                            @click="handleNoteEditCancel" 
                        >
                            <!-- <b-icon icon="x-circle" class="mr-2"></b-icon> -->
                            <span class="">{{ $t('buttons.cancel') }}</span>
                        </b-button>
                        <!-- <b-button v-if="isNoteDeletable && localEdit" key="editDeleteButton" @click="handleNoteDelete" class="ml-1" variant="danger">
                            <b-icon icon="trash" class="mr-2"></b-icon>
                            <span>Delete</span>
                        </b-button> -->
                        <b-button 
                            v-if="isNoteDeletable" 
                            :key="localEdit ? 'editDeleteButton' : 'readDeleteButton'" 
                            :id="`delete-popover-${localNote.id}`"
                            ref="deleteButton" 
                            :variant="localEdit ? 'danger' : 'subtle-danger'"
                            size="sm"
                            :disabled="loadingSave"
                            @click="handleNoteDelete" 
                        >
                            <b-icon icon="trash"></b-icon>
                            <span class="ml-2">{{ $t('vendorNoteLine.delete') }}</span>
                        </b-button>
                        <!-- :target="`delete-popover-${localNote.id}`"
                            triggers="click" -->
                        <b-popover
                            v-if="isNoteDeletable" 
                            :target="`delete-popover-${localNote.id}`"
                            triggers="click"
                            :show.sync="deletePopoverShowing"
                            :title="$t('vendorNoteLine.confirmTitle')"
                            placement="auto"
                            ref="deletepopover"
                            @show="onDeletePopoverShow"
                            @shown="onDeletePopoverShown"
                            @hidden="onDeletePopoverHidden"
                        >
                            <p class="font-size-90">{{ $t('vendorNoteLine.confirmDeleteMessage') }}</p>
                            <div class="d-flex align-items-center justify-content-center gap-3">
                                <b-button @click="onCancelDelete" size="md" variant="secondary">{{ $t('buttons.cancel') }}</b-button>
                                <b-button @click="onConfirmDelete" size="md" variant="danger">{{ $t('vendorNoteLine.delete') }}</b-button>
                            </div>
                        </b-popover>
                    </div>
                </div>
            </template>
            <div class="">
                <div ref="contentWrapper" class="content-wrapper position-relative" :style="noteContentWrapperStyles">
                    <div ref="contentArea" class="d-flex">
                        <div ref="bodyArea" class="w-70 p-0">
                            <div v-if="noteErrors.length && localEdit" class="alert alert-danger">
                                <p v-for="(errorMessage, index) in noteErrors" :key="`error_${index}`">{{ errorMessage }}</p>
                            </div>
                            <div 
                                v-if="!localEdit" 
                                v-on:dblclick="enterEditMode"
                            >
                                <quill-editor-full
                                    key="readonly-note-editor"
                                    content-height="bottom"
                                    :hide-toolbar="true"
                                    :disabled="true"
                                    :placeholder="$t('vendorNoteLine.emptyNotePlaceholder')"
                                    :value="localNote.body"
                                    v-model="localNote.body"
                                    @ready="onReadOnlyEditorReady"
                                ></quill-editor-full>
                            </div>
                            <div v-if="localEdit">
                                <quill-editor-full
                                    key="editable-note-editor"
                                    content-height="calc(100% - 15px)"
                                    :hide-toolbar="false"
                                    :disabled="loadingSave"
                                    :placeholder="$t('vendorNoteLine.writeNotePlaceholder')"
                                    :value="localNote.body"
                                    v-on:input="validateForm"
                                    v-model="localNote.body"
                                    @ready="onEditableEditorReady"
                                ></quill-editor-full>
                            </div>
                        </div>
                        <div ref="filesArea" class="w-30 files-separator-border">
                            <file-slinger-list-uploader
                                v-if="localEdit"
                                :key="`note-file-uploader-${localNote.id}`"
                                no-preview
                                small-list
                                :disabled="!localEdit"
                                :dropPlaceholderText="$t('vendorNoteLine.dropFilesPlaceholder')"
                                category="vendor_note"
                                :parent-id="localNote.id"
                                :existingFiles="localNote.files"
                                hide-button-labels
                                :maxFilesize="localMaxFilesize"
                                :maxItems="localMaxFiles"
                                class="h-100 bg-gray-50"
                                @update="handleFilesUpdate"
                            ></file-slinger-list-uploader>
                            <div v-else :key="`note-file-buttons-${localNote.id}`" class="h-100 px-2 py-2 bg-gray-50">
                                <div 
                                    v-if="localNote.files.length == 0"  
                                    key="noFiles" 
                                    class="h-100 w-100 d-flex align-items-center justify-content-center text-muted font-size-80 text-uppercase"
                                >
                                    {{ $t('vendorNoteLine.noFiles') }}
                                </div>
                                <div 
                                    v-else 
                                    key="hasFileList" 
                                    class="w-100 d-flex align-items-center justify-content-center text-dark font-size-70 text-uppercase"
                                >{{ $tc('vendorNoteLine.fileCount', localNote.files.length) }}</div>
                                <base-s-3-file-download-button
                                    v-for="file in localNote.files"
                                    :key="file.id"
                                    :id="file.id"
                                    :fileData="file"
                                    block
                                    buttonVariant="subtle-secondary"
                                    :showPopoverPreview="true"
                                ></base-s-3-file-download-button>
                            </div>
                        </div>
                    </div>
                    <!-- <div 
                        v-if="needsExpansion && !localEdit && !isExpanded"
                        class="expansion-gradient"
                        :style="{height: `${expansionGradientHeight}px`}"
                    ></div> -->
                </div>
                <div 
                    v-if="needsExpansion && !localEdit"
                    class="expansion-button-wrapper d-flex align-items-center justify-content-center" 
                    :style="{height: `${expansionButtonHeight}px`}"
                >
                    <b-button 
                        variant="bare-secondary"
                        class="w-100 h-100 no-outline text-uppercase text-primary d-flex align-items-center justify-content-center gap-3"
                        @click="toggleExpand" 
                    >
                        <div class="expansion-button-text">
                            <span class="font-size-80 font-weight-700">{{ isExpanded ? $t('vendorNoteLine.showLess') : $t('vendorNoteLine.showMore')}}</span>
                            <b-icon :icon="isExpanded ? 'chevron-up' : 'chevron-down'" class="font-size-80"></b-icon>
                        </div>
                    </b-button>
                </div>
            </div>
        </b-card>
    </div>
</template>


<script>
import FileSlingerListUploader from '../../s3_files/FileSlingerListUploader.vue'
import { suppliosApi } from '@/js/api-service';
import { FormatDateTimeLocal } from '@/js/filters-helpers';
import { errorHandling } from '@/js/custom-vue-mixins';
import cloneDeep from 'lodash/cloneDeep';
import BaseS3FileDownloadButton from '@/components/s3_files/BaseS3FileDownloadButton.vue';

export default {
    props: {
        companyId: { type: String, required: true },
        companyName: { type: String, required: true },
        note: { type: Object, required: true },
        editMode: { type: Boolean, required: false, default: false },  // only used to set initial value of localEdit
    },  //["companyId", "companyName", "note"],
    components: { FileSlingerListUploader, BaseS3FileDownloadButton },
    mixins: [ 
        errorHandling
    ],
    i18n: {
        messages: {
            en: {
                vendorNoteLine: {
                    edit: 'Edit',
                    delete: 'Delete',
                    confirmTitle: 'Please Confirm',
                    confirmDeleteMessage: 'Are you sure you want to delete this note?',
                    emptyNotePlaceholder: '(This note has no content)',
                    writeNotePlaceholder: 'Write your note...',
                    dropFilesPlaceholder: 'Add files to your note here',
                    noFiles: 'No Files',
                    fileCount: '{count} File | {count} Files',
                    showLess: 'Show Less',
                    showMore: 'Show More',
                    errorAddTitle: 'Add title to your note',
                },
            },
            es: {
                vendorNoteLine: {
                    edit: 'Editar',
                    delete: 'Eliminar',
                    confirmTitle: 'Por favor, confirme',
                    confirmDeleteMessage: '¿Está seguro de que desea eliminar esta nota?',
                    emptyNotePlaceholder: '(Esta nota no tiene contenido)',
                    writeNotePlaceholder: 'Escriba su nota...',
                    dropFilesPlaceholder: 'Agregue archivos a su nota aquí',
                    noFiles: 'Sin Archivos',
                    fileCount: '{count} Archivo | {count} Archivos',
                    showLess: 'Mostrar Menos',
                    showMore: 'Mostrar Más',
                    errorAddTitle: 'Agregue un título a su nota',
                },
            },
            pt: {
                vendorNoteLine: {
                    edit: 'Editar',
                    delete: 'Excluir',
                    confirmTitle: 'Por favor, confirme',
                    confirmDeleteMessage: 'Tem certeza de que deseja excluir esta nota?',
                    emptyNotePlaceholder: '(Esta nota não tem conteúdo)',
                    writeNotePlaceholder: 'Escreva sua nota...',
                    dropFilesPlaceholder: 'Adicione arquivos à sua nota aqui',
                    noFiles: 'Sem Arquivos',
                    fileCount: '{count} Arquivo | {count} Arquivos',
                    showLess: 'Mostrar Menos',
                    showMore: 'Mostrar Mais',
                    errorAddTitle: 'Adicione um título à sua nota',
                },
            },
            'zh-Hans': {
                vendorNoteLine: {
                    edit: '编辑',
                    delete: '删除',
                    confirmTitle: '请确认',
                    confirmDeleteMessage: '您确定要删除此备注吗？',
                    emptyNotePlaceholder: '（此备注没有内容）',
                    writeNotePlaceholder: '请输入备注内容...',
                    dropFilesPlaceholder: '在此处为备注添加文件',
                    noFiles: '暂无文件',
                    fileCount: '{count} 个文件 | {count} 个文件',
                    showLess: '收起',
                    showMore: '显示更多',
                    errorAddTitle: '请为备注添加标题',
                },
            },
            'zh-Hant': {
                vendorNoteLine: {
                    edit: '編輯',
                    delete: '刪除',
                    confirmTitle: '請確認',
                    confirmDeleteMessage: '您確定要刪除此備註嗎？',
                    emptyNotePlaceholder: '（此備註沒有內容）',
                    writeNotePlaceholder: '請輸入備註內容...',
                    dropFilesPlaceholder: '在此處為備註新增檔案',
                    noFiles: '沒有檔案',
                    fileCount: '{count} 個檔案 | {count} 個檔案',
                    showLess: '收合',
                    showMore: '顯示更多',
                    errorAddTitle: '請為備註加上標題',
                },
            },
        },
    },
    data: function() {
        return {
            localEdit: false,  // set to intial value of prop
            lastSavedNote: {},
            localNote: {},  // we clone the note prop object on create, keep all edits local, then if edit is cancelled we reclone from note prop, otherwise emit update to parent
            loadingSave: false,
            showDeleteConfirmationModal: false,
            noteErrors: [],
            deletePopoverShowing: false,
            maxDisplayHeight: 200,  // above this we make it expandable when NOT in edit mode
            expansionGradientHeight: 30,
            expansionButtonHeight: 50,
            contentMaxHeight: 'auto',  // set to other strings to control height 
            needsExpansion: false,  // set this in mounted hook
            isExpanded: false,
            noteContentWrapperStyles: {
                maxHeight: 'auto',
                transition: 'max-height 0.5s',
            },
            // FILE RELATED - allow overriding these per tenant later
            defaultMaxFilesize: (1024 * 1024 * 25),  // 25MB
            defaultMaxFiles: 20,
        }
    },
    computed: {
        isFormValid() {
            return this.noteErrors.length == 0;
        },
        formattedCreationDate() {
            return FormatDateTimeLocal(this.localNote.created_at);
        },
        isNoteEditable() {
            return this.localNote.permissions.includes('update')
        },
        isNoteDeletable() {
            return this.localNote.permissions.includes('delete')
        },
        localMaxFilesize() {
            // later, allow overriding these per tenant (which is why we have this in a computed)
            return this.defaultMaxFilesize || (1024 * 1024 * 25);  // 25MB is the default default
        },
        localMaxFiles() {
            // later, allow overriding these per tenant (which is why we have this in a computed)
            return this.defaultMaxFiles || 20;
        },
    },
    created() {
        this.localEdit = this.editMode;
        this.lastSavedNote = cloneDeep(this.note);
        this.localNote = cloneDeep(this.note);
    },
    mounted() {
        // this.updateExpansion();  // cannot do it here, since the editor is lazy loaded and may not be fully rendered yet, 
        // so we watch for the 'ready' event emitted by the editor to know when to update expansion based on content height
    },
    methods: {
        validateForm() {
            this.noteErrors = [];
            if(!this.localNote.title) { this.noteErrors.push(this.$t('vendorNoteLine.errorAddTitle')); }
            // empty body is OK for now 
            // if(!this.localNote.body) { this.noteErrors.push('Add content to your note'); }
        },
        async handleFilesUpdate(files) {
            console.log('files updated', files);
            this.$set(this.localNote, 'files', [...files]);
            this.$set(this.lastSavedNote, 'files', [...files]);
            // need to tell parent about it too, in case we click 'cancel', since it holds the source of truth 
            this.emitSavedNote();
        },
        emitSavedNote() {
            this.$emit('note-saved', this.lastSavedNote);
        },
        async handleNoteSave() {
            this.validateForm();
            if(!this.isFormValid) {
                return;
            }
            this.loadingSave = true;
            const noteData = {
                title: this.localNote.title,
                body: this.localNote.body,
            };
            try {
                await suppliosApi.vendorStandard.edit_note(this.localNote.id, this.companyId, noteData);
                // TODO - get updated note from server in response?  or just use local copy and assume correct?
                // tell parent to update its copy of note (we pass up cloned copy) -- it holds the latest saved data 
                this.lastSavedNote = cloneDeep(this.localNote);  // files should be OK as is here...?
                this.exitEditMode();
                this.emitSavedNote();
                //this.$emit('note-saved', updatedNote);
            }
            catch(error) {
                this.apiErrorHandler(error);
            }
            this.loadingSave = false;
        },
        handleNoteDelete() {
            //this.showDeleteConfirmationModal = true;
            this.deletePopoverShowing = true;
        },
        onReadOnlyEditorReady() {
            // called from the 'ready' event emitted by the read-only editor, which tells us when it is fully initialized and rendered, so we can do things that require accurate height measurements, etc.
            // needed because the editor is lazy loaded, so we cannot guarantee it is rendered in the mounted hook
            // for now we will just update expansion here since we need the editor to be fully rendered to get accurate height measurements
            this.updateExpansion();
        },
        onEditableEditorReady() {
            // called from the 'ready' event emitted by the editable editor, which tells us when it is fully initialized and rendered, so we can do things that require accurate height measurements, etc.
            // needed because the editor is lazy loaded, so we cannot guarantee it is rendered in the mounted hook
            // for now we will just update expansion here since we need the editor to be fully rendered to get accurate height measurements
            this.updateExpansion();
        },
        enterEditMode() {
            if (!this.isNoteEditable) { 
                return; 
            }
            this.localEdit = true;
            this.isExpanded = true;  // set this true also, so we keep it expanded after clicking save/cancel
            // this.$nextTick(() => {
            //     this.updateExpansion();
            // });  // we don't need this as long as we are listening to the @ready event from the editor to update expansion
        },
        exitEditMode() {
            this.localEdit = false;
            this.$nextTick(() => {
                this.updateExpansion();
            });  // we still keep this
        },
        handleNoteEditCancel() {
            // on cancel, we want to wipe away any local changes, re-clone from lastSavedNote
            //const localFiles = this.localNote.files?.length ? [...this.localNote.files] : [];
            this.localNote = cloneDeep(this.lastSavedNote);
            //this.localNote.files = localFiles;
            this.exitEditMode();
            this.$emit('note-edit-cancel', { noteId: this.localNote.id });   // no need for parent to do anything, we are not changing any data 
        },
        // DELETION -- parent actually does the delete, we just tell it to do so
        onCancelDelete() {
            this.deletePopoverShowing = false;
        },
        onConfirmDelete() {
            this.deletePopoverShowing = false;
            this.$emit('delete-note', { noteId: this.localNote.id });
        },
        onDeletePopoverShow() {
            // reset any stateful things that should be reset when popover is shown
        },
        onDeletePopoverShown() {
            // set focus to some button?
        },
        onDeletePopoverHidden() {
            this.focusRef(this.$refs.deleteButton)
        },
        focusRef(ref) {
            // for proper handling of focus on showing/hiding popover
            // Some references may be a component, functional component, or plain element
            // This handles that check before focusing, assuming a `focus()` method exists
            // We do this in a double `$nextTick()` to ensure components have
            // updated & popover positioned first
            this.$nextTick(() => {
                this.$nextTick(() => {
                    if (ref) {
                        (ref.$el || ref).focus();
                    }
                });
            });
        },
        // EXPANSION
        getRefHeight(ref) {
            if (ref) {
                return (ref.$el || ref).offsetHeight;
            }
        },
        updateExpansion() {
            // set all the things about being expandable/expanded
            const bodyHeight = this.getRefHeight(this.$refs.bodyArea);
            const filesHeight = this.getRefHeight(this.$refs.filesArea);
            const currentContentHeight = this.getRefHeight(this.$refs.contentArea);
            const neededContentHeight = Math.max(bodyHeight, filesHeight);
            this.needsExpansion = neededContentHeight > this.maxDisplayHeight;
            // if isExpanded was true (like before an edit), then deleted things so no longer needed, set it false here before continuing 
            if (!this.needsExpansion) {
                this.isExpanded = false;
            }
            const expandableHeight = this.isExpanded ? neededContentHeight : this.maxDisplayHeight;  // if we are showing the expansion toggle, we need extra room for that
            const contentMaxHeight = this.localEdit ? '100%' : (this.needsExpansion ? `${expandableHeight}px` : `${neededContentHeight}px`);
            this.noteContentWrapperStyles = {
                maxHeight: contentMaxHeight,
                transition: 'max-height 0.5s',
            };
            console.log(`updating contentMaxHeight on ${this.localNote.title} to ${this.contentMaxHeight} - currentContentHeight: ${currentContentHeight}, bodyHeight: ${bodyHeight}, filesHeight: ${filesHeight}, neededContentHeight: ${neededContentHeight}, expandableHeight: ${expandableHeight}`);
        },
        toggleExpand() {
            this.isExpanded = !this.isExpanded;
            this.updateExpansion();
        },

    }
};
</script>

<style lang="scss">
.vendor-note {
    .note-title-input {
        /* width: 30rem; */
        border: 1px dashed rgba(33, 33, 33, 0.2);
    }
    .files-separator-border {
        border-left: 1px solid rgba(33, 33, 33, 0.2);
    }

    /* .content-wrapper {
        transition: max-height 0.5s;  
    } */

    $expansion-gradient-base-color: rgb(255, 255, 255);  // white  or $gray-200;

    .expansion-gradient {
        background: linear-gradient(rgba(255, 255, 255, 0), rgba($expansion-gradient-base-color, 0.9));
        position: absolute;
        bottom: 0;
        width: 100%;
        //transition: height 0.5s;
    }
    .expansion-button-text {
        background-color: rgba($expansion-gradient-base-color, 0.9);
        padding: 0.5rem;
        display: flex;
        column-gap: 0.5rem;
        min-width: 120px;
        align-items: center;
        justify-content: center;
        border-radius: 0.5rem;
    }

    .expansion-button-wrapper {
        //background-color: rgba($gray-100, 0.8);
        background: linear-gradient(rgba(255, 255, 255, 0), rgba($expansion-gradient-base-color, 0.9));
        position: relative;
        bottom: 0;
        width: 100%;
    }
}


</style>
